<template>
  <div class="page-wrapper">
    <!-- 顶部标题区 -->
    <div class="header">
      <div class="header-bg">2025 資訊月 × 臺灣教育科技展</div>
      <h1 class="title">我的觀展足跡</h1>
    </div>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading">
      <p>加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
    </div>

    <!-- 内容区域 -->
    <div v-else-if="visitData" class="content">
      <!-- 时间轴记录 -->
      <div v-for="(record, index) in visitRecords" :key="index" class="visit-record">
        <div class="timeline">
          <div class="timeline-dot"></div>
          <div v-if="index < visitRecords.length - 1" class="timeline-line"></div>
        </div>

        <div class="record-content">
          <!-- 日期分组标题 -->
          <div v-if="shouldShowDate(index)" class="date-header">
            {{ formatDate(record.firstVisitTime) }}
          </div>

          <div class="record-card">
            <div class="time">{{ formatTime(record.firstVisitTime) }}</div>
            
            <!-- 展位名称 -->
            <div class="booth-name">{{ record.boothName }}</div>
            
            <!-- 公司简介 -->
            <div class="company-info">
              <div class="company-name">{{ record.companyName }}</div>
              <div class="company-detail">{{ record.companyInfo }}</div>
              <div class="booth-desc">{{ record.boothDescription }}</div>
            </div>

            <!-- 签到照片 -->
            <div v-if="getValidPhotos(record.checkinPhotos).length > 0" class="photos">
              <img 
                v-for="(photo, photoIndex) in getValidPhotos(record.checkinPhotos)" 
                :key="photoIndex"
                :src="photo" 
                :alt="`签到照片${photoIndex + 1}`"
                class="photo"
                @error="handleImageError"
              />
            </div>

            <!-- 评论 -->
            <div v-if="record.comments && record.comments.length > 0" class="comments">
              <div class="comment-title">我的評論</div>
              <div v-for="(comment, commentIndex) in record.comments" :key="commentIndex" class="comment">
                {{ comment }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部总结 -->
      <div class="summary-footer">
        <h2 class="summary-title">元邦導覽員幫你總結</h2>
        <div class="summary-stats">
          <p>共參觀了 <strong>{{ visitData.totalBoothsVisited }}</strong> 個展位</p>
          <!-- <p>拍攝了 <strong>{{ visitData.totalPhotos }}</strong> 張照片</p> -->
          <p>留下了 <strong>{{ visitData.totalComments }}</strong> 條評論</p>
        </div>
        
        <!-- AI总结文本 -->
        <div v-if="visitData.textSummary" class="text-summary">
          <div class="summary-label">AI 總結</div>
          <div class="summary-text">{{ visitData.textSummary }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ItMonth25Summary',
  data() {
    return {
      summaryId: '',
      visitData: null,
      loading: false,
      error: null
    }
  },
  computed: {
    visitRecords() {
      if (!this.visitData || !this.visitData.visitRecords) return []
      // 按时间排序
      return [...this.visitData.visitRecords].sort((a, b) => {
        return new Date(a.firstVisitTime) - new Date(b.firstVisitTime)
      })
    }
  },
  created() {
    this.summaryId = this.$route.params.xxxxx
    this.loadVisitHistory()
  },
  watch: {
    '$route.params.xxxxx'(newValue) {
      this.summaryId = newValue
      this.loadVisitHistory()
    }
  },
  methods: {
    async loadVisitHistory() {
      // 尝试从 localStorage 加载
      const cacheKey = `visit_history_${this.summaryId}`
      const cachedData = localStorage.getItem(cacheKey)
      
      if (cachedData) {
        try {
          this.visitData = JSON.parse(cachedData)
          console.log('从缓存加载数据')
        } catch (e) {
          console.error('解析缓存数据失败:', e)
        }
      }

      // 发送 API 请求获取最新数据
      this.loading = true
      this.error = null

      try {
        const response = await fetch(
          `https://api.vrstate.com/vrstate-platform/itmonth25-service/exhibition/visit-history/${this.summaryId}`
        )
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const result = await response.json()
        
        if (result.code === 200 && result.data) {
          this.visitData = result.data
          // 保存到 localStorage
          localStorage.setItem(cacheKey, JSON.stringify(result.data))
          console.log('数据已保存到 localStorage')
        } else {
          throw new Error(result.message || '获取数据失败')
        }
      } catch (err) {
        console.error('获取访问历史失败:', err)
        this.error = `加载失败: ${err.message}`
        
        // 如果没有缓存数据，显示错误
        if (!this.visitData) {
          this.error = '无法加载数据，请检查网络连接'
        }
      } finally {
        this.loading = false
      }
    },
    
    formatDate(dateString) {
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}/${month}/${day}`
    },
    
    formatTime(dateString) {
      const date = new Date(dateString)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    
    shouldShowDate(index) {
      if (index === 0) return true
      const currentDate = this.formatDate(this.visitRecords[index].firstVisitTime)
      const prevDate = this.formatDate(this.visitRecords[index - 1].firstVisitTime)
      return currentDate !== prevDate
    },
    
    getValidPhotos(photos) {
      if (!photos) return []
      return photos.filter(photo => photo && photo.trim() !== '')
    },
    
    handleImageError(event) {
      event.target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.page-wrapper {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding-bottom: 40px;
}

/* 顶部区域 */
.header {
  background: linear-gradient(135deg, #2196F3 0%, #00BCD4 100%);
  color: white;
  padding: 20px;
  text-align: center;
}

.header-bg {
  font-size: 14px;
  margin-bottom: 10px;
  opacity: 0.9;
}

.title {
  font-size: 24px;
  font-weight: bold;
  margin: 10px 0;
  color: white;
}

/* 加载和错误状态 */
.loading, .error {
  text-align: center;
  padding: 40px 20px;
  font-size: 16px;
}

.error {
  color: #f44336;
}

/* 内容区域 */
.content {
  padding: 0 16px;
  max-width: 600px;
  margin: 0 auto;
}

/* 日期标题 */
.date-header {
  font-size: 14px;
  color: #666;
  margin: 24px 0 12px 0;
  font-weight: 500;
}

/* 访问记录 */
.visit-record {
  display: flex;
  position: relative;
  margin-bottom: 0;
}

/* 时间轴 */
.timeline {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 16px;
  padding-top: 8px;
}

.timeline-dot {
  width: 12px;
  height: 12px;
  background-color: #f44336;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 3px rgba(244, 67, 54, 0.2);
}

.timeline-line {
  width: 2px;
  flex: 1;
  background-color: #f44336;
  margin-top: 4px;
  min-height: 40px;
}

/* 记录卡片 */
.record-content {
  flex: 1;
  margin-bottom: 16px;
}

.record-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.time {
  font-size: 13px;
  color: #999;
  margin-bottom: 8px;
}

.booth-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  background-color: #f0f0f0;
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  display: inline-block;
}

/* 公司信息 */
.company-info {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 12px;
}

.company-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.company-detail {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}

.booth-desc {
  font-size: 13px;
  color: #888;
  margin-top: 6px;
}

/* 照片网格 */
.photos {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 12px 0;
}

.photo {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 8px;
  background-color: #f0f0f0;
  border: 1px solid #e0e0e0;
}

/* 评论区 */
.comments {
  margin-top: 12px;
}

.comment-title {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  font-weight: 500;
}

.comment {
  background-color: #f5f5f5;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  line-height: 1.5;
  margin-bottom: 8px;
}

.comment:last-child {
  margin-bottom: 0;
}

/* 底部总结 */
.summary-footer {
  margin-top: 32px;
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.summary-title {
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  text-align: center;
}

.summary-stats {
  background-color: #fafafa;
  padding: 16px;
  border-radius: 8px;
}

.summary-stats p {
  font-size: 14px;
  color: #666;
  margin: 8px 0;
  line-height: 1.6;
}

.summary-stats strong {
  color: #2196F3;
  font-size: 16px;
  font-weight: 600;
}

/* AI总结文本 */
.text-summary {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
}

.summary-label {
  font-size: 14px;
  color: #2196F3;
  font-weight: 600;
  margin-bottom: 12px;
}

.summary-text {
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  white-space: pre-wrap;
  background: linear-gradient(135deg, #f0f9ff 0%, #fafafa 100%);
  padding: 16px;
  border-radius: 8px;
  border-left: 3px solid #2196F3;
}
</style>
